import { Box, Typography, IconButton, Menu, MenuItem, Chip, TextField, InputAdornment, Button, Stack } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import { useMemo, useState } from 'react';
import { KINDS, KIND_LABEL, KIND_COLOR, kindIcon, formatHours, formatMoney } from './constants';

/**
 * Recursive tree. Props:
 *   nodes          — array of { ...project, children }
 *   selectedId     — currently selected project id
 *   expanded       — Set<id>
 *   onToggle(id)   — toggle expansion
 *   onSelect(id)
 *   onAction(id, action)  — 'rename' | 'move' | 'addChild' | 'delete'
 *   search         — string filter (name includes, case-insensitive)
 *   kindFilter     — '' or a kind
 */
function TreeRow({ node, depth, selectedId, expanded, onToggle, onSelect, onAction, searchLc, kindFilter }) {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expanded.has(node.id);
  const isSelected = selectedId === node.id;
  const Icon = kindIcon(node.kind, isExpanded);

  // Filtering: node visible if it matches (or any descendant matches).
  const selfMatches = (!searchLc || node.name?.toLowerCase().includes(searchLc))
    && (!kindFilter || node.kind === kindFilter);
  const visibleChildren = (node.children || [])
    .map((c) => <TreeRow
      key={c.id}
      node={c}
      depth={depth + 1}
      selectedId={selectedId}
      expanded={expanded}
      onToggle={onToggle}
      onSelect={onSelect}
      onAction={onAction}
      searchLc={searchLc}
      kindFilter={kindFilter}
    />)
    .filter(Boolean);

  // If self doesn't match but no visible children, skip entirely
  if (!selfMatches && visibleChildren.every((c) => c === null)) {
    // Actually we need to check if any child is non-null (rendered). Since filter already drops nulls:
    if (visibleChildren.length === 0) return null;
  }

  return (
    <Box>
      <Box
        onClick={() => onSelect(node.id)}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          pl: `${12 + depth * 16}px`,
          pr: 1,
          py: 0.75,
          cursor: 'pointer',
          borderLeft: '3px solid',
          borderColor: isSelected ? KIND_COLOR[node.kind] || '#A78BFA' : 'transparent',
          bgcolor: isSelected ? 'action.selected' : 'transparent',
          '&:hover': { bgcolor: 'action.hover' },
          gap: 0.5,
          minHeight: { xs: 44, md: 36 },
        }}
      >
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); if (hasChildren) onToggle(node.id); }}
          sx={{ p: { xs: 1, md: 0.25 }, visibility: hasChildren ? 'visible' : 'hidden' }}
        >
          {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>
        <Icon sx={{ fontSize: 18, color: KIND_COLOR[node.kind] || 'text.secondary', flexShrink: 0 }} />
        <Typography
          variant="body2"
          title={node.name}
          sx={{
            flex: 1, ml: 0.5, minWidth: 0,
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
            lineHeight: 1.25,
            fontWeight: isSelected ? 600 : 400,
          }}
        >
          {node.kind === 'segment' && node.name.includes('—')
            ? node.name.split('—').slice(1).join('—').trim()
            : node.name}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums', minWidth: 46, textAlign: 'right', flexShrink: 0 }}>
          {formatHours(node.subtree_hours)}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums', minWidth: 48, textAlign: 'right', flexShrink: 0 }}>
          {formatMoney(node.subtree_expenses)}
        </Typography>
        <RowMenu onAction={(a) => onAction(node.id, a)} />
      </Box>
      {isExpanded && visibleChildren}
    </Box>
  );
}

function RowMenu({ onAction }) {
  const [anchor, setAnchor] = useState(null);
  const close = () => setAnchor(null);
  return (
    <>
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }} sx={{ p: { xs: 1, md: 0.25 } }}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={!!anchor} onClose={close} onClick={(e) => e.stopPropagation()}>
        <MenuItem onClick={() => { close(); onAction('addChild'); }}>New sub-project</MenuItem>
        <MenuItem onClick={() => { close(); onAction('rename'); }}>Rename / Edit</MenuItem>
        <MenuItem onClick={() => { close(); onAction('move'); }}>Move to…</MenuItem>
        <MenuItem onClick={() => { close(); onAction('delete'); }} sx={{ color: 'error.main' }}>Delete</MenuItem>
      </Menu>
    </>
  );
}

export default function ProjectTree({
  tree,
  selectedId,
  expanded,
  onToggle,
  onSelect,
  onAction,
  onNewRoot,
  onExpandAll,
  onCollapseAll,
}) {
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const searchLc = search.trim().toLowerCase();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search projects…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ mb: 1 }}
        />
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          <Chip label="All" size="small" color={kindFilter === '' ? 'primary' : 'default'} onClick={() => setKindFilter('')} />
          {KINDS.map((k) => (
            <Chip key={k} label={KIND_LABEL[k]} size="small"
              color={kindFilter === k ? 'primary' : 'default'}
              onClick={() => setKindFilter(kindFilter === k ? '' : k)}
              sx={{ borderLeft: `3px solid ${KIND_COLOR[k]}` }}
            />
          ))}
        </Stack>
        <Button
          fullWidth
          size="small"
          startIcon={<AddIcon />}
          onClick={onNewRoot}
          sx={{ mt: 1 }}
          variant="outlined"
        >
          New root project
        </Button>
        {(onExpandAll || onCollapseAll) && (
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            {onExpandAll && (
              <Button fullWidth size="small" startIcon={<UnfoldMoreIcon />} onClick={onExpandAll}>
                Expand all
              </Button>
            )}
            {onCollapseAll && (
              <Button fullWidth size="small" startIcon={<UnfoldLessIcon />} onClick={onCollapseAll}>
                Collapse all
              </Button>
            )}
          </Stack>
        )}
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', py: 0.5 }}>
        {tree.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
            No projects yet. Click "New root project" to begin.
          </Typography>
        )}
        {tree.map((n) => (
          <TreeRow
            key={n.id}
            node={n}
            depth={0}
            selectedId={selectedId}
            expanded={expanded}
            onToggle={onToggle}
            onSelect={onSelect}
            onAction={onAction}
            searchLc={searchLc}
            kindFilter={kindFilter}
          />
        ))}
      </Box>
    </Box>
  );
}
